import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import apiService from '../services/api';
import { PROVIDER_DELETE_PREVIEW_LABELS } from '../constants/providerDeletionLabels';

interface ProviderDeletionSectionProps {
  providerId: number;
  providerName: string;
  onDeleted: () => void;
}

const ProviderDeletionSection: React.FC<ProviderDeletionSectionProps> = ({
  providerId,
  providerName,
  onDeleted,
}) => {
  const [confirmName, setConfirmName] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const { data: preview, isLoading: previewLoading, error: previewError } = useQuery({
    queryKey: ['provider-delete-preview', providerId],
    queryFn: () => apiService.getProviderDeletionPreview(providerId),
    enabled: providerId > 0,
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiService.deleteProvider(providerId),
    onSuccess: () => {
      setDeleteError('');
      onDeleted();
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      setDeleteError(data?.error || data?.message || 'La suppression a échoué.');
    },
  });

  const nameMatches = confirmName.trim() === providerName.trim();

  return (
    <Box sx={{ mt: 1 }}>
      <Alert severity="error" sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Suppression définitive et irréversible
        </Typography>
        <Typography variant="body2">
          Cette action supprime définitivement le partenaire « {providerName} » et l&apos;ensemble des
          données qui lui sont rattachées. Aucune restauration n&apos;est possible.
        </Typography>
      </Alert>

      <Typography variant="body1" paragraph>
        Seront notamment supprimés ou détachés de façon permanente :
      </Typography>

      <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 3 }}>
        <li>
          <Typography variant="body2">
            Les annonces classiques et les annonces liées (clients), ainsi que leurs images sur le serveur
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Le catalogue d&apos;objets trouvés, les conversations et tous les messages échangés
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Les matchings, les lieux / points du partenaire, les événements et le logo
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Les comptes utilisateurs rattachés à ce partenaire (accès dashboard inclus)
          </Typography>
        </li>
      </Box>

      {previewLoading && (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={28} />
        </Box>
      )}

      {previewError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Impossible de charger le bilan des éléments liés.
        </Alert>
      )}

      {preview && (
        <>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {preview.warning}
          </Alert>
          <Typography variant="subtitle2" gutterBottom>
            Bilan des éléments concernés
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 3 }}>
            {Object.entries(preview.counts).map(([key, val]) => (
              <li key={key}>
                <Typography variant="body2">
                  {PROVIDER_DELETE_PREVIEW_LABELS[key] ?? key} :{' '}
                  {typeof val === 'boolean' ? (val ? 'Oui' : 'Non') : val}
                </Typography>
              </li>
            ))}
          </Box>
        </>
      )}

      <Typography variant="subtitle2" gutterBottom>
        Confirmation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pour confirmer, saisissez exactement le nom du partenaire :{' '}
        <strong>{providerName}</strong>
      </Typography>

      <TextField
        fullWidth
        label="Nom du partenaire"
        value={confirmName}
        onChange={(e) => {
          setConfirmName(e.target.value);
          setDeleteError('');
        }}
        placeholder={providerName}
        disabled={deleteMutation.isPending}
        sx={{ mb: 2 }}
        autoComplete="off"
      />

      {deleteError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {deleteError}
        </Alert>
      )}

      <Button
        variant="contained"
        color="error"
        disabled={!nameMatches || deleteMutation.isPending || previewLoading}
        onClick={() => deleteMutation.mutate()}
      >
        {deleteMutation.isPending ? 'Suppression en cours…' : 'Supprimer définitivement ce partenaire'}
      </Button>
    </Box>
  );
};

export default ProviderDeletionSection;
